import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '@app/hooks/stores';
import { Table, Tooltip } from 'antd';
import { CustomIcon } from '@app/modules/Layout/customIcon';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import RoomModal from '@app/common/RoomModal';
import { ROOM_MODAL_TAB_KEYS } from '@app/constants';
import AlarmsModal from '@app/common/AlarmsModal';
import { generateDefaultTablePagination, isUnitOld } from '@app/utils/helpers';
import { CUSTOM_ICONS } from '@app/types/common';

dayjs.extend(duration);

interface RoomStatusProps {
  handleDateUpdate: () => void;
}

const connectionColumnStyles = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

const RoomsStatus: React.FC<RoomStatusProps> = ({ handleDateUpdate }) => {
  const { dashboardStore } = useStores();

  const {
    selectedBuildingId,
    loadingRoom,
    rooms,
    dubaiTZ,
    actionLoading,
    getRoomAlarmsById,
    getRoomDataById,
    roomData,
    getRoomMeasurementsById,
    page,
    pagination,
    limit,
    changePage,
    handleTableFilterAndSort
  } = dashboardStore;

  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedRoomData, setSelectedRoomData] = useState<Record<string, string>>({});
  const [alarmsModalVisible, setAlarmsModalVisible] = useState<boolean>(false);

  const roomsData = rooms[selectedBuildingId || 0];

  const fetchRoomDataById = (id: string) => {
    getRoomMeasurementsById(id);
    getRoomAlarmsById(id);
  };

  const handleAlarmsModal = () => {
    setAlarmsModalVisible(!alarmsModalVisible);
  };

  const handleRoomDataModalOpen = (roomId: string, roomName: string) => () => {
    handleDateUpdate();
    if (!isModalVisible) fetchRoomDataById(roomId);
    const roomReport = roomsData.find(building => building.roomId === Number(roomId));
    if (roomReport) {
      setSelectedRoomData({
        roomName: roomReport.roomName,
        buildingName: roomReport.buildingName,
        levelName: roomReport.levelName
      });
    }
    handleRoomModal();
  };

  const handleRoomModal = () => {
    setModalVisible(!isModalVisible);
  };

  const handleAlarmModalOpen = (roomId: string) => () => {
    getRoomAlarmsById(roomId);
    if (!selectedRoomData.roomName) {
      const roomReport = roomsData.find(building => building.roomId === Number(roomId));
      if (roomReport) {
        setSelectedRoomData({
          roomName: roomReport.roomName,
          buildingName: roomReport.buildingName,
          levelName: roomReport.levelName
        });
      }
    }
    handleAlarmsModal();
  };

  const handleModalTabChange = (key: string) => {
    if (key === ROOM_MODAL_TAB_KEYS.Alarm) {
      getRoomAlarmsById();
    }

    if (key === ROOM_MODAL_TAB_KEYS.Data && !roomData?.roomInfo) {
      getRoomDataById();
    }
  };

  const tableColumns = [
    {
      title: 'Room',
      width: 120,
      align: 'center' as const,
      render: value => {
        const oldUnit = isUnitOld(value.lastActiveAt);

        return (
          <div
            className={`${oldUnit ? 'old-unit' : 'actionColumn'}`}
            onClick={oldUnit ? undefined : handleRoomDataModalOpen(value.roomId, value.roomName)}
          >
            {value.roomName}
          </div>
        );
      }
    },
    {
      title: 'Average Temperature(°C)',
      dataIndex: 'temp1',
      width: 30,
      align: 'center' as const,
      sorter: true,
      render: temp1 => {
        return !!temp1 ? <span>{temp1}</span> : null;
      }
    },
    {
      title: 'AC operation',
      dataIndex: 'fanTime',
      width: 30,
      align: 'center' as const,
      sorter: true,
      render: fanTime => {
        if (fanTime === 0) return <CustomIcon type={CUSTOM_ICONS.FAN_OFF} />;
        if (fanTime >= 1) return <CustomIcon type={CUSTOM_ICONS.FAN_ON_ONE} />;
        return null;
      }
    },
    {
      title: 'Current Power',
      dataIndex: 'activeEnergy',
      width: 40,
      align: 'center' as const,
      sorter: true,
      render: activeEnergy => {
        return !!activeEnergy ? <span>{activeEnergy?.toFixed(1)} W</span> : null;
      }
    },
    {
      title: 'Motion Activity',
      width: 40,
      align: 'center' as const,
      render: value => {
        if (value.isOldUnit) return null;
        if (value.motionActivity === 1)
          return (
            <Tooltip title="medium movement">
              <>
                <CustomIcon type={CUSTOM_ICONS.MOTION_ON} />
              </>
            </Tooltip>
          );
        if (value.motionActivity === 2)
          return (
            <Tooltip title="high movement">
              <CustomIcon type={CUSTOM_ICONS.MOTION_ON} />
              <CustomIcon type={CUSTOM_ICONS.MOTION_ON} />
            </Tooltip>
          );
        return (
          <Tooltip title="no movement">
            <>
              <CustomIcon type={CUSTOM_ICONS.MOTION_OFF} />
            </>
          </Tooltip>
        );
      }
    },
    {
      title: 'Alarms',
      width: 40,
      align: 'center' as const,
      sorter: true,
      render: value => (
        <div className="actionColumn" onClick={handleAlarmModalOpen(value.roomId)}>
          {!!value.alarmsCount && !value.isOldUnit && `${value.alarmsCount} alarms`}
        </div>
      )
    },
    {
      title: 'Devices ID',
      dataIndex: 'deviceId',
      width: 40,
      align: 'center' as const
    },
    {
      title: 'Connection',
      dataIndex: 'lastActiveAt',
      width: 60,
      align: 'center' as const,
      sorter: true,
      render: lastActiveAt => (
        <span style={connectionColumnStyles}>
          {isUnitOld(lastActiveAt) ? (
            <CustomIcon type={CUSTOM_ICONS.CONNECTION_OFF} />
          ) : (
            <CustomIcon type={CUSTOM_ICONS.CONNECTION_ON} />
          )}
          <span style={{ paddingLeft: 10 }}>
            {lastActiveAt ? dayjs(lastActiveAt).utcOffset(dubaiTZ).fromNow() : 'never'}
          </span>
        </span>
      )
    }
  ];

  const handleTableChange = async (tablePagination: any, columnFilters: any, sorter: any) => {
    if (tablePagination?.current !== page || tablePagination?.pageSize !== limit) {
      const { current, pageSize } = tablePagination;
      return changePage(current, pageSize);
    }
    await handleTableFilterAndSort(sorter);
  };

  const fullRoomData = { ...roomData, extraData: selectedRoomData };

  return (
    <>
      <AlarmsModal
        isModalVisible={alarmsModalVisible}
        data={fullRoomData}
        handleOk={handleAlarmsModal}
        loading={actionLoading}
      />
      <RoomModal
        isModalVisible={isModalVisible}
        handleOk={handleRoomModal}
        handleCancel={handleRoomModal}
        data={fullRoomData}
        onTabChange={handleModalTabChange}
        loading={actionLoading}
        timeZoneOffset={dubaiTZ}
      />
      <Table
        className="dashboard-table"
        dataSource={roomsData}
        columns={tableColumns}
        rowKey={record => `${record.roomId}`}
        onChange={handleTableChange}
        //@ts-ignore
        pagination={generateDefaultTablePagination({ page, limit: limit, total: pagination?.total })}
        // scroll={{ y: 300 }}
        size="small"
        loading={loadingRoom}
      />
    </>
  );
};

export default observer(RoomsStatus);
